import React, { ReactElement, useContext } from 'react'

import Fieldset from '../../Common/Components/Fieldset'
import Heading from '../../Common/Components/Heading'
import FormRow from '../../Common/Components/FormRow'
import { Select, SelectOptions } from '../../Common/Components/Selects'
import TextInput from '../../Common/Components/TextInput'

import {
    REPORT_VALUE_COMPARISON_TYPE_BETWEEN,
    REPORT_VALUE_COMPARISON_TYPE_EQUAL,
    REPORT_VALUE_COMPARISON_TYPE_HIGHERTHAN,
    REPORT_VALUE_COMPARISON_TYPE_HIGHERTHANOREQUAL,
    REPORT_VALUE_COMPARISON_TYPE_LOWERTHAN,
    REPORT_VALUE_COMPARISON_TYPE_LOWERTHANOREQUAL,
    REPORT_VALUE_COMPARISON_TYPE_NONE,
    REPORT_VALUE_COMPARISON_TYPE_NOTBETWEEN,
    REPORT_VALUE_COMPARISON_TYPE_NOTEQUAL
} from './Constants'

import { LanguageContext } from '../../Common/Contexts'

import { DurationFiltering as DurationFilteringLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'

// CSS imports. Commented out because of current build process.
// import './durationFiltering.scss'

/**
 * Component properties type.
 */
type Props = {
    ringTimeDistComparisonType: number
    setRingTimeDistComparisonType: (newValue: number) => void
    ringTimeDistValue1: string
    setRingTimeDistValue1: (newValue: string) => void
    ringTimeDistValue2: string
    setRingTimeDistValue2: (newValue: string) => void
    ringTimeSegmentComparisonType: number
    setRingTimeSegmentComparisonType: (newValue: number) => void
    ringTimeSegmentValue1: string
    setRingTimeSegmentValue1: (newValue: string) => void
    ringTimeSegmentValue2: string
    setRingTimeSegmentValue2: (newValue: string) => void
    callTalkTimeComparisonType: number
    setCallTalkTimeComparisonType: (newValue: number) => void
    callTalkTimeValue1: string
    setCallTalkTimeValue1: (newValue: string) => void
    callTalkTimeValue2: string
    setCallTalkTimeValue2: (newValue: string) => void
    totalCallTimeComparisonType: number
    setTotalCallTimeComparisonType: (newValue: number) => void
    totalCallTimeValue1: string
    setTotalCallTimeValue1: (newValue: string) => void
    totalCallTimeValue2: string
    setTotalCallTimeValue2: (newValue: string) => void
    callHeldTimeComparisonType: number
    setCallHeldTimeComparisonType: (newValue: number) => void
    callHeldTimeValue1: string
    setCallHeldTimeValue1: (newValue: string) => void
    callHeldTimeValue2: string
    setCallHeldTimeValue2: (newValue: string) => void
    filterDurationRingSupported: boolean
    filterDurationsOtherSupported: boolean
}
/**
 * @param ringTimeDistComparisonType Report property.
 * @param setRingTimeDistComparisonType Setter for corresponding report property.
 * @param ringTimeDistValue1 Report property.
 * @param setRingTimeDistValue1 Setter for corresponding report property.
 * @param ringTimeDistValue2 Report property.
 * @param setRingTimeDistValue2 Setter for corresponding report property.
 * @param ringTimeSegmentComparisonType Report property.
 * @param setRingTimeSegmentComparisonType Setter for corresponding report property.
 * @param ringTimeSegmentValue1 Report property.
 * @param setRingTimeSegmentValue1 Setter for corresponding report property.
 * @param ringTimeSegmentValue2 Report property.
 * @param setRingTimeSegmentValue2 Setter for corresponding report property.
 * @param callTalkTimeComparisonType Report property.
 * @param setCallTalkTimeComparisonType Setter for corresponding report property.
 * @param callTalkTimeValue1 Report property.
 * @param setCallTalkTimeValue1 Setter for corresponding report property.
 * @param callTalkTimeValue2 Report property.
 * @param setCallTalkTimeValue2 Setter for corresponding report property.
 * @param totalCallTimeComparisonType Report property.
 * @param setTotalCallTimeComparisonType Setter for corresponding report property.
 * @param totalCallTimeValue1 Report property.
 * @param setTotalCallTimeValue1 Setter for corresponding report property.
 * @param totalCallTimeValue2 Report property.
 * @param setTotalCallTimeValue2 Setter for corresponding report property.
 * @param callHeldTimeComparisonType Report property.
 * @param setCallHeldTimeComparisonType Setter for corresponding report property.
 * @param callHeldTimeValue1 Report property.
 * @param setCallHeldTimeValue1 Setter for corresponding report property.
 * @param callHeldTimeValue2 Report property.
 * @param setCallHeldTimeValue2 Setter for corresponding report property.
 * @param filterDurationRingSupported Report definition property.
 * @param filterDurationsOtherSupported Report definition property - whether other duration filters are allowed (e.g. call time, call held time...)
 */
const DurationFiltering: React.FC<Props> = ({
    ringTimeDistComparisonType,
    setRingTimeDistComparisonType,
    ringTimeDistValue1,
    setRingTimeDistValue1,
    ringTimeDistValue2,
    setRingTimeDistValue2,
    ringTimeSegmentComparisonType,
    setRingTimeSegmentComparisonType,
    ringTimeSegmentValue1,
    setRingTimeSegmentValue1,
    ringTimeSegmentValue2,
    setRingTimeSegmentValue2,
    callTalkTimeComparisonType,
    setCallTalkTimeComparisonType,
    callTalkTimeValue1,
    setCallTalkTimeValue1,
    callTalkTimeValue2,
    setCallTalkTimeValue2,
    totalCallTimeComparisonType,
    setTotalCallTimeComparisonType,
    totalCallTimeValue1,
    setTotalCallTimeValue1,
    totalCallTimeValue2,
    setTotalCallTimeValue2,
    callHeldTimeComparisonType,
    setCallHeldTimeComparisonType,
    callHeldTimeValue1,
    setCallHeldTimeValue1,
    callHeldTimeValue2,
    setCallHeldTimeValue2,
    filterDurationsOtherSupported,
    filterDurationRingSupported
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('DurationFiltering')

    const timeComparisonTypeOptions: SelectOptions<number> = [
        {
            value: REPORT_VALUE_COMPARISON_TYPE_NONE,
            label: language.get(DurationFilteringLanguage.TimeComparisonAnyOptionLabel)
        },
        {
            value: REPORT_VALUE_COMPARISON_TYPE_HIGHERTHAN,
            label: language.get(DurationFilteringLanguage.TimeComparisonHigherthanOptionLabel)
        },
        {
            value: REPORT_VALUE_COMPARISON_TYPE_HIGHERTHANOREQUAL,
            label: language.get(DurationFilteringLanguage.TimeComparisonHigherequalOptionLabel)
        },
        {
            value: REPORT_VALUE_COMPARISON_TYPE_LOWERTHAN,
            label: language.get(DurationFilteringLanguage.TimeComparisonLowerthanOptionLabel)
        },
        {
            value: REPORT_VALUE_COMPARISON_TYPE_LOWERTHANOREQUAL,
            label: language.get(DurationFilteringLanguage.TimeComparisonLowerequalOptionLabel)
        },
        {
            value: REPORT_VALUE_COMPARISON_TYPE_BETWEEN,
            label: language.get(DurationFilteringLanguage.TimeComparisonBetweenOptionLabel)
        },
        {
            value: REPORT_VALUE_COMPARISON_TYPE_EQUAL,
            label: language.get(DurationFilteringLanguage.TimeComparisonEqualOptionLabel)
        },
        {
            value: REPORT_VALUE_COMPARISON_TYPE_NOTEQUAL,
            label: language.get(DurationFilteringLanguage.TimeComparisonNotequalOptionLabel)
        },
        {
            value: REPORT_VALUE_COMPARISON_TYPE_NOTBETWEEN,
            label: language.get(DurationFilteringLanguage.TimeComparisonNotbetweenOptionLabel)
        }
    ]

    const timeComparisonTypesWithTwoRangeValues = [
        REPORT_VALUE_COMPARISON_TYPE_BETWEEN,
        REPORT_VALUE_COMPARISON_TYPE_NOTBETWEEN
    ]

    /**
     * Checks whether the time comparison type is one of the types that needs two values to be configured.
     * @param type Time comparison type to check.
     */
    const isTimeComparisonTypeWithTwoRangeValues = (type: number): boolean =>
        timeComparisonTypesWithTwoRangeValues.indexOf(type) !== -1

    // Duration Filtering flags.
    const isCallHeldTimeSelected = callHeldTimeComparisonType !== REPORT_VALUE_COMPARISON_TYPE_NONE
    const isTotalCallTimeSelected = totalCallTimeComparisonType !== REPORT_VALUE_COMPARISON_TYPE_NONE
    const isCallTalkTimeSelected = callTalkTimeComparisonType !== REPORT_VALUE_COMPARISON_TYPE_NONE
    const isRingTimeSegmentSelected = ringTimeSegmentComparisonType !== REPORT_VALUE_COMPARISON_TYPE_NONE
    const isRingTimeDistSelected = ringTimeDistComparisonType !== REPORT_VALUE_COMPARISON_TYPE_NONE

    return (
        <Fieldset className='duration-filtering'>
            <Heading level={HeadingLevel.ONE}>
                {language.get(DurationFilteringLanguage.DurationFiltering_SectionHeader)}
            </Heading>
            {filterDurationRingSupported && (
                <>
                    <FormRow isInline={isRingTimeDistSelected} className={'ring-time-distribution'}>
                        <Select
                            label={language.get(DurationFilteringLanguage.RingtimeDistributionLabel)}
                            value={ringTimeDistComparisonType}
                            handleChange={setRingTimeDistComparisonType}
                            options={timeComparisonTypeOptions}
                        />
                        <div className='time-values'>
                            {isRingTimeDistSelected && (
                                <TextInput
                                    value={ringTimeDistValue1}
                                    onChange={setRingTimeDistValue1}
                                    labelLeft={language.get(DurationFilteringLanguage.TimeComparisonValue1Label)}
                                />
                            )}
                            {isTimeComparisonTypeWithTwoRangeValues(ringTimeDistComparisonType) && (
                                <TextInput
                                    value={ringTimeDistValue2}
                                    onChange={setRingTimeDistValue2}
                                    labelLeft={language.get(DurationFilteringLanguage.TimeComparisonValue2Label)}
                                />
                            )}
                        </div>
                    </FormRow>
                    <FormRow isInline={isRingTimeSegmentSelected} className={'ringtime-segment'}>
                        <Select
                            label={language.get(DurationFilteringLanguage.RingtimeSegmentLabel)}
                            value={ringTimeSegmentComparisonType}
                            handleChange={setRingTimeSegmentComparisonType}
                            options={timeComparisonTypeOptions}
                        />
                        <div className='time-values'>
                            {isRingTimeSegmentSelected && (
                                <TextInput
                                    value={ringTimeSegmentValue1}
                                    onChange={setRingTimeSegmentValue1}
                                    labelLeft={language.get(DurationFilteringLanguage.TimeComparisonValue1Label)}
                                />
                            )}
                            {isTimeComparisonTypeWithTwoRangeValues(ringTimeSegmentComparisonType) && (
                                <TextInput
                                    value={ringTimeSegmentValue2}
                                    onChange={setRingTimeSegmentValue2}
                                    labelLeft={language.get(DurationFilteringLanguage.TimeComparisonValue2Label)}
                                />
                            )}
                        </div>
                    </FormRow>
                </>
            )}

            {filterDurationsOtherSupported && (
                <>
                    <FormRow isInline={isCallTalkTimeSelected} className={'call-talk-time'}>
                        <Select
                            label={language.get(DurationFilteringLanguage.CalltalktimeLabel)}
                            value={callTalkTimeComparisonType}
                            handleChange={setCallTalkTimeComparisonType}
                            options={timeComparisonTypeOptions}
                        />
                        <div className='time-values'>
                            {isCallTalkTimeSelected && (
                                <TextInput
                                    value={callTalkTimeValue1}
                                    onChange={setCallTalkTimeValue1}
                                    labelLeft={language.get(DurationFilteringLanguage.TimeComparisonValue1Label)}
                                />
                            )}
                            {isTimeComparisonTypeWithTwoRangeValues(callTalkTimeComparisonType) && (
                                <TextInput
                                    value={callTalkTimeValue2}
                                    onChange={setCallTalkTimeValue2}
                                    labelLeft={language.get(DurationFilteringLanguage.TimeComparisonValue2Label)}
                                />
                            )}
                        </div>
                    </FormRow>
                    <FormRow isInline={isTotalCallTimeSelected} className={'total-call-time'}>
                        <Select
                            label={language.get(DurationFilteringLanguage.TotalcalltimeLabel)}
                            value={totalCallTimeComparisonType}
                            handleChange={setTotalCallTimeComparisonType}
                            options={timeComparisonTypeOptions}
                        />
                        <div className='time-values'>
                            {isTotalCallTimeSelected && (
                                <TextInput
                                    value={totalCallTimeValue1}
                                    onChange={setTotalCallTimeValue1}
                                    labelLeft={language.get(DurationFilteringLanguage.TimeComparisonValue1Label)}
                                />
                            )}
                            {isTimeComparisonTypeWithTwoRangeValues(totalCallTimeComparisonType) && (
                                <TextInput
                                    value={totalCallTimeValue2}
                                    onChange={setTotalCallTimeValue2}
                                    labelLeft={language.get(DurationFilteringLanguage.TimeComparisonValue2Label)}
                                />
                            )}
                        </div>
                    </FormRow>
                    <FormRow isInline={isCallHeldTimeSelected} className={'call-held-time'}>
                        <Select
                            label={language.get(DurationFilteringLanguage.CallheldtimeLabel)}
                            value={callHeldTimeComparisonType}
                            handleChange={setCallHeldTimeComparisonType}
                            options={timeComparisonTypeOptions}
                        />
                        <div className='time-values'>
                            {isCallHeldTimeSelected && (
                                <TextInput
                                    value={callHeldTimeValue1}
                                    onChange={setCallHeldTimeValue1}
                                    labelLeft={language.get(DurationFilteringLanguage.TimeComparisonValue1Label)}
                                />
                            )}
                            {isTimeComparisonTypeWithTwoRangeValues(callHeldTimeComparisonType) && (
                                <TextInput
                                    value={callHeldTimeValue2}
                                    onChange={setCallHeldTimeValue2}
                                    labelLeft={language.get(DurationFilteringLanguage.TimeComparisonValue2Label)}
                                />
                            )}
                        </div>
                    </FormRow>
                </>
            )}
        </Fieldset>
    )
}

export default React.memo(DurationFiltering)
